import { toast } from 'sonner';

export interface EnhancedDocument {
  id: string;
  name: string;
  description: string;
  category: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  fileData: string; // base64 encoded file data
  uploadDate: string;
  createdAt: string;
  updatedAt: string;
  chunkIds?: string[]; // For large files split into chunks
  studentId?: string; // Optional student association
  studentName?: string; // Optional student name for display
}

interface DocumentChunk {
  id: string;
  documentId: string;
  chunkIndex: number;
  data: string;
  totalChunks: number;
}

const DOCUMENTS_STORAGE_KEY = 'nalc_documents';
const DOCUMENTS_BACKUP_KEY_1 = 'nalc_documents_backup_1';
const DOCUMENTS_BACKUP_KEY_2 = 'nalc_documents_backup_2';
const DOCUMENTS_EMERGENCY_KEY = 'nalc_documents_emergency';
const CHUNK_STORAGE_PREFIX = 'nalc_doc_chunk_';
const MAX_CHUNK_SIZE = 4000000; // 4MB per chunk for better performance
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB maximum file size

export class EnhancedDocumentStorage {
  private static instance: EnhancedDocumentStorage;

  static getInstance(): EnhancedDocumentStorage {
    if (!EnhancedDocumentStorage.instance) {
      EnhancedDocumentStorage.instance = new EnhancedDocumentStorage();
    }
    return EnhancedDocumentStorage.instance;
  }

  private validateFileSize(fileSize: number): boolean {
    if (fileSize > MAX_FILE_SIZE) {
      console.warn(`File size ${fileSize} exceeds maximum allowed size of ${MAX_FILE_SIZE}`);
      return false;
    }
    return true; // Allow files up to 100MB
  }

  private checkStorageSpace(): boolean {
    try {
      // Estimate available storage - allow up to 100MB+ total storage
      const currentUsage = this.calculateStorageSize();
      const maxAllowedStorage = 120 * 1024 * 1024; // 120MB total limit
      
      if (currentUsage >= maxAllowedStorage) {
        console.warn('Storage limit approaching, consider cleanup');
        return false;
      }
      
      return true;
    } catch (error) {
      console.warn('Storage check failed, allowing operation');
      return true; // Allow operation if check fails
    }
  }

  private clearOldTempData(): void {
    const keysToRemove = [];
    for (let key in localStorage) {
      if (key.startsWith('temp_') || key.startsWith('old_')) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (e) {
        // Ignore cleanup errors
      }
    });
  }

  private performStorageCleanup(): void {
    console.log('🧹 Performing minimal storage cleanup...');
    
    try {
      const keysToRemove = [];
      for (let key in localStorage) {
        if (key.startsWith('temp_') || key.startsWith('cache_') || key.startsWith('session_')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));
      console.log('🧹 Cleaned temporary data only');
    } catch (error) {
      console.warn('Cleanup encountered issues but continuing');
    }
  }

  private compressData(data: string): string {
    try {
      return btoa(unescape(encodeURIComponent(data)));
    } catch (error) {
      console.warn('Compression failed, using original data');
      return data;
    }
  }

  private decompressData(data: string): string {
    try {
      return decodeURIComponent(escape(atob(data)));
    } catch (error) {
      console.warn('Decompression failed, using original data');
      return data;
    }
  }

  private chunkDocument(document: EnhancedDocument): EnhancedDocument {
    console.log(`📦 Processing large document: ${document.name} (${document.fileSize} bytes)`);

    if (document.fileData.length <= MAX_CHUNK_SIZE) {
      return document;
    }

    const chunks: DocumentChunk[] = [];
    const totalChunks = Math.ceil(document.fileData.length / MAX_CHUNK_SIZE);
    const chunkIds: string[] = [];

    console.log(`📦 Chunking document: ${document.name} into ${totalChunks} chunks (4MB each)`);

    for (let i = 0; i < totalChunks; i++) {
      const chunkId = `${document.id}_chunk_${i}`;
      const startIndex = i * MAX_CHUNK_SIZE;
      const endIndex = Math.min(startIndex + MAX_CHUNK_SIZE, document.fileData.length);
      const chunkData = document.fileData.slice(startIndex, endIndex);

      const chunk: DocumentChunk = {
        id: chunkId,
        documentId: document.id,
        chunkIndex: i,
        data: this.compressData(chunkData),
        totalChunks
      };

      chunks.push(chunk);
      chunkIds.push(chunkId);

      try {
        localStorage.setItem(`${CHUNK_STORAGE_PREFIX}${chunkId}`, JSON.stringify(chunk));
        console.log(`📦 Stored chunk ${i + 1}/${totalChunks} (4MB)`);
      } catch (error) {
        console.warn(`Chunk storage issue for chunk ${i}:`, error);
      }
    }

    return {
      ...document,
      fileData: '',
      chunkIds
    };
  }

  private reconstructDocument(document: EnhancedDocument): EnhancedDocument {
    if (!document.chunkIds || document.chunkIds.length === 0) {
      return document;
    }

    let reconstructedData = '';
    const sortedChunkIds = document.chunkIds.sort((a, b) => {
      const aIndex = parseInt(a.split('_chunk_')[1]);
      const bIndex = parseInt(b.split('_chunk_')[1]);
      return aIndex - bIndex;
    });

    for (const chunkId of sortedChunkIds) {
      try {
        const chunkData = localStorage.getItem(`${CHUNK_STORAGE_PREFIX}${chunkId}`);
        if (chunkData) {
          const chunk: DocumentChunk = JSON.parse(chunkData);
          reconstructedData += this.decompressData(chunk.data);
        }
      } catch (error) {
        console.warn('Failed to reconstruct document chunk:', error);
      }
    }

    return {
      ...document,
      fileData: reconstructedData
    };
  }

  // Enhanced save with 100MB+ support
  async saveDocuments(documents: EnhancedDocument[]): Promise<void> {
    try {
      console.log(`💾 Saving ${documents.length} documents with 100MB+ capacity`);

      // Validate total storage usage
      const totalSize = documents.reduce((sum, doc) => sum + doc.fileSize, 0);
      console.log(`📊 Total documents size: ${this.formatBytes(totalSize)}`);

      const processedDocuments = documents.map(doc => {
        if (doc.fileData.length > MAX_CHUNK_SIZE) {
          return this.chunkDocument(doc);
        }
        return doc;
      });

      const documentData = JSON.stringify(processedDocuments);
      const timestamp = new Date().toISOString();

      // Primary save with error handling
      try {
        localStorage.setItem(DOCUMENTS_STORAGE_KEY, documentData);
        localStorage.setItem(`${DOCUMENTS_STORAGE_KEY}_timestamp`, timestamp);
        console.log(`💾 Primary storage saved successfully (100MB+ capacity)`);
      } catch (primaryError) {
        console.error('Primary storage failed:', primaryError);
        throw primaryError;
      }

      // Multiple backup saves (don't fail if backups fail)
      const backupKeys = [DOCUMENTS_BACKUP_KEY_1, DOCUMENTS_BACKUP_KEY_2, DOCUMENTS_EMERGENCY_KEY];
      
      for (const backupKey of backupKeys) {
        try {
          localStorage.setItem(backupKey, documentData);
          localStorage.setItem(`${backupKey}_timestamp`, timestamp);
        } catch (backupError) {
          console.warn(`Backup ${backupKey} failed:`, backupError);
        }
      }

      console.log(`💾 Successfully saved ${documents.length} documents with 100MB+ capacity`);
    } catch (error) {
      console.error('Storage save error:', error);
      throw error;
    }
  }

  // Enhanced load with fallback recovery
  async loadDocuments(): Promise<EnhancedDocument[]> {
    const storageKeys = [
      DOCUMENTS_STORAGE_KEY, 
      DOCUMENTS_BACKUP_KEY_1, 
      DOCUMENTS_BACKUP_KEY_2, 
      DOCUMENTS_EMERGENCY_KEY
    ];
    
    for (const key of storageKeys) {
      try {
        const data = localStorage.getItem(key);
        const timestamp = localStorage.getItem(`${key}_timestamp`);
        
        if (data) {
          const documents: EnhancedDocument[] = JSON.parse(data);
          
          // Reconstruct chunked documents
          const reconstructedDocuments = documents.map(doc => {
            if (doc.chunkIds && doc.chunkIds.length > 0) {
              return this.reconstructDocument(doc);
            }
            return doc;
          });

          console.log(`📁 Loaded ${reconstructedDocuments.length} documents from ${key} (saved: ${timestamp})`);
          
          // If not from primary, restore to primary
          if (key !== DOCUMENTS_STORAGE_KEY && reconstructedDocuments.length > 0) {
            try {
              await this.saveDocuments(reconstructedDocuments);
              console.log(`🔄 Restored ${reconstructedDocuments.length} documents to primary storage`);
            } catch (restoreError) {
              console.warn('Failed to restore to primary storage:', restoreError);
            }
          }
          
          return reconstructedDocuments;
        }
      } catch (error) {
        console.warn(`Failed to load from ${key}:`, error);
      }
    }

    console.log('📁 No documents found in any storage location, starting fresh');
    return [];
  }

  async addDocument(document: EnhancedDocument): Promise<void> {
    try {
      console.log(`📤 Adding document with 100MB+ capacity: ${document.name} (${this.formatBytes(document.fileSize)})`);
      
      // Validate file size (up to 100MB)
      if (!this.validateFileSize(document.fileSize)) {
        throw new Error(`File size exceeds maximum limit of ${this.formatBytes(MAX_FILE_SIZE)}`);
      }
      
      const documents = await this.loadDocuments();
      const updatedDocuments = [...documents, document];
      
      await this.saveDocuments(updatedDocuments);
      
      console.log(`✅ Document saved with 100MB+ capacity: ${document.name}`);
    } catch (error) {
      console.error('Add document error:', error);
      throw error;
    }
  }

  async deleteDocument(documentId: string): Promise<void> {
    const documents = await this.loadDocuments();
    const documentToDelete = documents.find(doc => doc.id === documentId);
    
    // Delete chunks if they exist
    if (documentToDelete?.chunkIds) {
      for (const chunkId of documentToDelete.chunkIds) {
        try {
          localStorage.removeItem(`${CHUNK_STORAGE_PREFIX}${chunkId}`);
        } catch (error) {
          console.warn(`Failed to delete chunk ${chunkId}`);
        }
      }
    }

    const updatedDocuments = documents.filter(doc => doc.id !== documentId);
    await this.saveDocuments(updatedDocuments);
  }

  getStorageStats(): {
    totalDocuments: number;
    totalSize: number;
    totalSizeFormatted: string;
    chunkedDocuments: number;
    storageUsage: string;
    maxCapacity: string;
  } {
    try {
      const documentsData = localStorage.getItem(DOCUMENTS_STORAGE_KEY);
      const documents = documentsData ? JSON.parse(documentsData) : [];
      const totalSize = this.calculateStorageSize();
      const chunkedDocuments = documents.filter((doc: EnhancedDocument) => doc.chunkIds && doc.chunkIds.length > 0).length;

      return {
        totalDocuments: documents.length,
        totalSize,
        totalSizeFormatted: this.formatBytes(totalSize),
        chunkedDocuments,
        storageUsage: this.getStorageUsagePercentage(),
        maxCapacity: this.formatBytes(MAX_FILE_SIZE) + ' per file, 120MB total'
      };
    } catch (error) {
      return {
        totalDocuments: 0,
        totalSize: 0,
        totalSizeFormatted: '0 B',
        chunkedDocuments: 0,
        storageUsage: '0%',
        maxCapacity: '100MB per file, 120MB total'
      };
    }
  }

  private calculateStorageSize(): number {
    let size = 0;
    for (let key in localStorage) {
      if (key.startsWith('nalc_doc') || key === DOCUMENTS_STORAGE_KEY) {
        try {
          size += localStorage[key].length;
        } catch (error) {
          // Skip problematic keys
        }
      }
    }
    return size;
  }

  private formatBytes(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  private getStorageUsagePercentage(): string {
    try {
      const used = JSON.stringify(localStorage).length;
      const quota = 120 * 1024 * 1024; // 120MB total capacity
      const percentage = ((used / quota) * 100);
      return percentage.toFixed(1) + '%';
    } catch (error) {
      return 'Available';
    }
  }

  async clearAllDocuments(): Promise<void> {
    try {
      const storageKeys = [
        DOCUMENTS_STORAGE_KEY,
        DOCUMENTS_BACKUP_KEY_1,
        DOCUMENTS_BACKUP_KEY_2,
        DOCUMENTS_EMERGENCY_KEY
      ];
      
      storageKeys.forEach(key => {
        try {
          localStorage.removeItem(key);
          localStorage.removeItem(`${key}_timestamp`);
        } catch (error) {
          console.warn(`Failed to clear ${key}`);
        }
      });
    } catch (error) {
      console.warn('Some storage clearing failed');
    }

    const keysToRemove = [];
    for (let key in localStorage) {
      if (key.startsWith(CHUNK_STORAGE_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => {
      try {
        localStorage.removeItem(key);
      } catch (error) {
        console.warn(`Failed to remove ${key}`);
      }
    });

    console.log('🗑️ All documents cleared');
  }
}

export const enhancedDocumentStorage = EnhancedDocumentStorage.getInstance();
